import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { loadRecaptcha } from '../../common/recaptcha';

// The reCAPTCHA v2 checkbox, rendered explicitly into a div this component
// owns. Generic (ocl_online#170, #208): it reports the token (null when it
// expires or errors) and a load failure, and the parent decides what to show.
// ref.reset() clears it after a failed submit — a token is single-use.
// `load` is injectable so tests never add a real <script>.
const RecaptchaCheckbox = React.forwardRef(({ siteKey, onToken, onLoadError, load = loadRecaptcha }, ref) => {
  const { i18n } = useTranslation();
  const container = React.useRef(null);
  const widget = React.useRef(null);
  const api = React.useRef(null);
  const callbacks = React.useRef({ onToken, onLoadError });
  callbacks.current = { onToken, onLoadError };

  React.useImperativeHandle(ref, () => ({
    reset: () => {
      if (api.current && widget.current !== null)
        api.current.reset(widget.current);
      callbacks.current.onToken(null);
    },
  }));

  React.useEffect(() => {
    let active = true;
    // i18n.language, not resolvedLanguage: the latter stays 'en' until the
    // visitor's lazy locale bundle is registered, which is usually after
    // this effect runs on first mount (src/i18n/config.js:58-60).
    load({ language: i18n.language }).then(grecaptcha => {
      if (!active || !container.current)
        return;
      api.current = grecaptcha;
      widget.current = grecaptcha.render(container.current, {
        sitekey: siteKey,
        callback: token => callbacks.current.onToken(token),
        'expired-callback': () => callbacks.current.onToken(null),
        'error-callback': () => callbacks.current.onToken(null),
      });
    }).catch(() => {
      if (active)
        callbacks.current.onLoadError();
    });
    return () => { active = false; };
  }, [siteKey]);

  // 78px is the checkbox's height; reserving it stops the form jumping when it loads.
  return <Box ref={container} sx={{ minHeight: 78 }} />;
});

export default RecaptchaCheckbox;
